import type { CostListFilters } from './costs.js';

export type CostProjectScope = 'all' | 'company' | 'project';
export interface CostFilterState {
  type: 'all' | 'RECEIPT' | 'TRAVEL';
  status: 'all' | 'PENDING' | 'APPROVED' | 'REJECTED';
  sync: 'all' | 'NOT_SYNCED' | 'SYNCING' | 'SYNCED' | 'ERROR';
  projectScope: CostProjectScope;
  projectId: string;
  query: string;
  from: string;
  to: string;
}
export type CostScopeContext = { projectScope?: CostProjectScope; projectId?: string;
  costFilters?: { status?: 'PENDING' | 'APPROVED' | 'REJECTED'; sync?: 'NOT_SYNCED' | 'SYNCING' | 'SYNCED' | 'ERROR' } };
export const COST_FILTER_STATE_KEY = 'costs:filters';
export const EMPTY_COST_FILTERS: CostFilterState = {
  type: 'all', status: 'all', sync: 'all', projectScope: 'all', projectId: '', query: '', from: '', to: '',
};
const text = (value: unknown) => typeof value === 'string' ? value : '';
function option<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? value as T : fallback;
}

/** Accept old desktop state and mobile ALL values without mistaking 'all' for a project. */
export function restoreCostFilters(value: unknown, context?: CostScopeContext): CostFilterState {
  const stored = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const id = text(stored.projectId).trim();
  const projectId = id && id.toLowerCase() !== 'all' ? id : '';
  const projectScope = option(stored.projectScope, ['all', 'company', 'project'] as const, projectId ? 'project' : 'all');
  const restored: CostFilterState = {
    type: option(stored.type, ['all', 'RECEIPT', 'TRAVEL'] as const, 'all'),
    status: option(stored.status, ['all', 'PENDING', 'APPROVED', 'REJECTED'] as const, 'all'),
    sync: option(stored.sync, ['all', 'NOT_SYNCED', 'SYNCING', 'SYNCED', 'ERROR'] as const, 'all'),
    projectScope: projectScope === 'project' && !projectId ? 'all' : projectScope,
    projectId: projectScope === 'project' ? projectId : '',
    query: text(stored.query), from: text(stored.from), to: text(stored.to),
  };
  return applyCostScope(restored, context);
}

export function applyCostScope(state: CostFilterState, context?: CostScopeContext): CostFilterState {
  // An explicit dashboard queue is a complete query, not another condition on
  // an unrelated saved search/date/type. Ordinary project launches keep filters.
  if (context?.costFilters) state = { ...EMPTY_COST_FILTERS,
    status: option(context.costFilters.status, ['all', 'PENDING', 'APPROVED', 'REJECTED'] as const, 'all'),
    sync: option(context.costFilters.sync, ['all', 'NOT_SYNCED', 'SYNCING', 'SYNCED', 'ERROR'] as const, 'all') };
  if (!context?.projectScope && !context?.projectId) return state;
  const scope = context.projectScope || 'project';
  // Keep an invalid explicit project visibly unselected; never turn it into a company expense.
  return { ...state, projectScope: scope, projectId: scope === 'project' ? text(context.projectId).trim() : '' };
}

export function costFilterQuery(state: CostFilterState): CostListFilters {
  return { type: state.type, reviewStatus: state.status, syncStatus: state.sync,
    projectScope: state.projectScope, ...(state.projectScope === 'project' ? { projectId: state.projectId } : {}),
    q: state.query.trim(), from: state.from, to: state.to };
}

/** Undefined asks for an explicit attribution; null deliberately means company/no project. */
export function costCaptureProject(state: CostScopeContext): string | null | undefined {
  if (state.projectScope === 'company') return null;
  if (state.projectScope === 'project' || (!state.projectScope && state.projectId)) return state.projectId || undefined;
  return undefined;
}

/** List and summary share this exact contract. Pagination never narrows summary totals. */
export function buildCostQuery(filters: CostListFilters = {}, pagination = true): string {
  const projectId = text(filters.projectId).trim();
  const scope = filters.projectScope || (projectId && projectId !== 'all' ? 'project' : 'all');
  if (!['all', 'company', 'project'].includes(scope) ||
      (scope === 'project' && (!projectId || projectId === 'all')) ||
      (filters.projectScope && scope !== 'project' && projectId)) {
    throw Object.assign(new Error('Invalid cost project scope'), { code: 'COSTS_INVALID_PROJECT_SCOPE' });
  }
  const params = new URLSearchParams({ projectScope: scope });
  if (scope === 'project') params.set('projectId', projectId);
  const shared = ['type', 'reviewStatus', 'syncStatus', 'q', 'from', 'to'] as const;
  for (const key of shared) {
    const value = filters[key];
    if (value == null || value === '' || (['type', 'reviewStatus', 'syncStatus'].includes(key) && value === 'all')) continue;
    params.set(key, String(value));
  }
  if (pagination) {
    if (filters.limit != null) params.set('limit', String(filters.limit));
    if (filters.cursor) params.set('cursor', filters.cursor);
  }
  return '?' + params.toString();
}

export function appendCostPage<T extends { id: string }>(current: T[], page: T[]): T[] {
  const seen = new Set(current.map(item => item.id));
  return [...current, ...page.filter(item => !seen.has(item.id) && !!seen.add(item.id))];
}
